import { useEffect } from "react";
import { Target, Users, Award, TrendingUp, BarChart3, Shield } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SEO } from "@/components/seo";

const values = [
  {
    icon: BarChart3,
    title: "Professional-Grade Tools",
    description:
      "Every template and indicator is built to institutional standards, giving retail traders access to the same analytical power used by professionals.",
  },
  {
    icon: Shield,
    title: "Reliability First",
    description:
      "Our products are rigorously tested across multiple market conditions to ensure consistent, dependable performance when it matters most.",
  },
  {
    icon: Users,
    title: "Community Driven",
    description:
      "We listen to our traders. Feature requests, improvements, and new products are shaped by real feedback from active market participants.",
  },
  {
    icon: TrendingUp,
    title: "Continuous Improvement",
    description:
      "Markets evolve, and so do our tools. We regularly update our products to stay ahead of changing market dynamics and platform updates.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="About Us"
        description="Learn about Sierra Chart Pro — our mission to empower traders with professional-grade templates, indicators, and tools for Sierra Chart."
        keywords="about Sierra Chart Pro, trading tools company, Sierra Chart templates team, professional trading indicators"
        canonical="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Sierra Chart Pro",
          description:
            "Premium Sierra Chart templates and custom indicators for professional traders.",
          url: "https://sierrachartpro.com",
        }}
      />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="text-about-title">
        About Sierra Chart Pro
      </h1>
      <p className="text-muted-foreground mb-10" data-testid="text-about-subtitle">
        Empowering traders with professional-grade Sierra Chart tools since day one.
      </p>

      <section className="mb-10" data-testid="section-who-we-are">
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Who We Are</h2>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            Sierra Chart Pro is a team of experienced traders and developers who
            share a passion for market analysis and the Sierra Chart platform. We
            recognized early on that while Sierra Chart is one of the most
            powerful charting platforms available, setting it up for optimal
            performance requires significant time and expertise.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            That realization drove us to create a curated library of
            professional templates and custom indicators that let traders skip
            the steep learning curve and jump straight into informed
            decision-making. Our team combines decades of live trading
            experience with deep technical knowledge of Sierra Chart's study and
            charting engine.
          </p>
        </div>
      </section>

      <section className="mb-10" data-testid="section-our-mission">
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Our Mission</h2>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            Our mission is simple: make professional-level market analysis
            accessible to every Sierra Chart user. Whether you trade futures,
            forex, or equities, we believe the right tools can transform how you
            read and react to the markets.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We are committed to delivering high-quality, ready-to-use charting
            solutions that save traders hours of configuration time. Every
            product we release is designed, tested, and refined by traders who
            use these tools in live markets every single day.
          </p>
        </div>
      </section>

      <section className="mb-10" data-testid="section-why-choose-us">
        <div className="flex items-center gap-2 mb-3">
          <Award className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Why Choose Us</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {values.map((item) => (
            <Card key={item.title} data-testid={`card-value-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-medium text-sm">{item.title}</h3>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="mb-4" data-testid="section-our-story">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Our Story</h2>
        </div>
        <div className="rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            Sierra Chart Pro started from a simple frustration. As active
            traders, we spent countless hours configuring Sierra Chart —
            building Market Profile layouts, tuning Volume Profile studies,
            setting up Footprint and Orderflow charts, and tweaking DOM
            configurations. Every time a new version dropped or we switched
            machines, the process started all over again.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed mb-3">
            We began sharing our setups with fellow traders in online
            communities. The response was overwhelming. Traders were not only
            grateful for the time saved but also discovered analytical
            approaches they had never considered. That feedback loop — building,
            sharing, refining — became the foundation of Sierra Chart Pro.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Today, our product catalog spans templates for Market Profile TPO,
            Footprint Charts, Volume Profiles, Heatmaps, and DOM setups, along
            with custom-coded indicators for Orderflow analysis, absorption
            detection, and volume sequencing. Each product reflects real trading
            workflows honed over years of live market experience. We continue to
            grow our library based on trader feedback and evolving market needs.
          </p>
        </div>
      </section>
    </div>
  );
}
